import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import axios from 'axios'
import { loginWithPasskey as passkeyLoginFlow } from '@/auth/passkeys'

interface LocalUser {
  id: string
  email: string
  name: string
  role: string
  is_super_admin: boolean
  // live | demo | calibration. The server enforces the calibration lock;
  // this drives the nav so a scoped user is not shown doors that 403.
  environment?: string
  is_trial: boolean
  trial_status?: string
  trial_expires_at?: string | null
  trial_started_at?: string | null
  trial_termination_scheduled_at?: string | null
  trial_terminated_at?: string | null
  trial_agreement_required?: boolean
  trial_agreement_accepted_at?: string | null
  trial_agreement_version?: string | null
  nda_required?: boolean
  nda_accepted_at?: string | null
  nda_version?: string | null
  // module id -> granted actions, resolved server-side by app/core/rbac.py so
  // the nav cannot offer a module whose endpoints would 403. Optional because
  // a profile cached before this shipped will not carry it.
  permissions?: Record<string, string[]>
}

interface LocalAuthState {
  isLocalAuth: boolean
  localUser: LocalUser | null
  localToken: string | null
  // `token` is a session only when neither flag is set. With requires_2fa it is
  // a short-lived challenge token; with requires_2fa_enrolment (the platform
  // requires two-factor and this account has none yet) it is an enrolment token
  // that reaches only the 2FA set-up endpoints. Neither is ever stored as a session.
  login: (
    email: string,
    password: string,
  ) => Promise<{ requires_2fa: boolean; requires_2fa_enrolment: boolean; token: string }>
  loginWithPasskey: (email: string) => Promise<void>
  verify2FA: (code: string, partialToken: string) => Promise<void>
  // Completes a sign-in that was diverted through enrolment: the server hands
  // back the session when the first authenticator code is confirmed.
  finishEnrolment: (accessToken: string, user: LocalUser) => void
  register: (email: string, password: string, name: string) => Promise<void>
  refreshUser: () => Promise<void>
  acceptTrialAgreement: () => Promise<void>
  logout: () => void
}

const LocalAuthContext = createContext<LocalAuthState>({
  isLocalAuth: false,
  localUser: null,
  localToken: null,
  login: async () => ({ requires_2fa: false, requires_2fa_enrolment: false, token: '' }),
  loginWithPasskey: async () => {},
  verify2FA: async () => {},
  finishEnrolment: () => {},
  register: async () => {},
  refreshUser: async () => {},
  acceptTrialAgreement: async () => {},
  logout: () => {},
})

// eslint-disable-next-line react-refresh/only-export-components
export function useLocalAuth() {
  return useContext(LocalAuthContext)
}

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1'

export default function LocalAuthProvider({ children }: { children: ReactNode }) {
  const [localUser, setLocalUser] = useState<LocalUser | null>(() => {
    const stored = localStorage.getItem('cmos_user')
    return stored ? JSON.parse(stored) : null
  })
  const [localToken, setLocalToken] = useState<string | null>(() => localStorage.getItem('cmos_token'))

  const clearSession = useCallback(() => {
    setLocalToken(null)
    setLocalUser(null)
    localStorage.removeItem('cmos_token')
    localStorage.removeItem('cmos_user')
  }, [])

  const persistSession = useCallback((token: string, user: LocalUser) => {
    setLocalToken(token)
    setLocalUser(user)
    localStorage.setItem('cmos_token', token)
    localStorage.setItem('cmos_user', JSON.stringify(user))
  }, [])

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('cmos_token')
    if (!token) return
    const res = await axios.get<LocalUser>(`${API_BASE}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    setLocalUser(res.data)
    localStorage.setItem('cmos_user', JSON.stringify(res.data))
  }, [])

  // Revalidate the cached profile on every boot, not only when it is absent.
  // `environment` and `role` are authorization inputs — Layout builds the nav
  // from environment — and the cached copy is as old as the last sign-in. A
  // stale snapshot shows a scope the server no longer agrees with, in either
  // direction. The cached value still renders immediately; this corrects it.
  useEffect(() => {
    if (!localToken) return
    axios
      .get<LocalUser>(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${localToken}` },
      })
      .then((res) => {
        // Merge, never replace. /auth/me is filtered through a response model
        // and login is not, so the two payloads can drift; a field missing from
        // the narrower one must not delete an authorization flag we already
        // hold. Dropping nda_required this way silently unmounts the NDA gate.
        const cached = localStorage.getItem('cmos_user')
        const prev = cached ? (JSON.parse(cached) as LocalUser) : null
        const merged = { ...(prev ?? {}), ...res.data }
        setLocalUser(merged)
        localStorage.setItem('cmos_user', JSON.stringify(merged))
      })
      .catch((err) => {
        // Only a rejected identity ends the session. A transient network or
        // 5xx blip must not sign out someone whose token is still good.
        const status = err?.response?.status
        if (status === 401 || status === 403) clearSession()
      })
  }, [clearSession, localToken])

  const login = useCallback(async (email: string, password: string) => {
    const res = await axios.post(`${API_BASE}/auth/login`, { email, password })
    const { access_token, requires_2fa, requires_2fa_enrolment, user } = res.data

    if (requires_2fa) {
      return { requires_2fa: true, requires_2fa_enrolment: false, token: access_token }
    }
    if (requires_2fa_enrolment) {
      return { requires_2fa: false, requires_2fa_enrolment: true, token: access_token }
    }

    persistSession(access_token, user)
    return { requires_2fa: false, requires_2fa_enrolment: false, token: access_token }
  }, [persistSession])

  const verify2FA = useCallback(async (code: string, partialToken: string) => {
    const res = await axios.post(
      `${API_BASE}/auth/verify-2fa`,
      { code },
      { headers: { Authorization: `Bearer ${partialToken}` } },
    )
    const { access_token, user } = res.data
    persistSession(access_token, user)
  }, [persistSession])

  const finishEnrolment = useCallback(
    (accessToken: string, user: LocalUser) => persistSession(accessToken, user),
    [persistSession],
  )

  const loginWithPasskey = useCallback(async (email: string) => {
    const begin = await axios.post(`${API_BASE}/auth/passkeys/login/begin`, { email })
    const finished = await passkeyLoginFlow(email, begin.data.options)
    const res = await axios.post(`${API_BASE}/auth/passkeys/login/finish`, finished)
    persistSession(res.data.access_token, res.data.user)
  }, [persistSession])

  const register = useCallback(async (email: string, password: string, name: string) => {
    await axios.post(`${API_BASE}/auth/register`, { email, password, name })
  }, [])

  const acceptTrialAgreement = useCallback(async () => {
    if (!localToken) return
    const res = await axios.post(
      `${API_BASE}/auth/trial-agreement/accept`,
      {},
      { headers: { Authorization: `Bearer ${localToken}` } },
    )
    const user = res.data?.user as LocalUser | undefined
    if (user) {
      setLocalUser(user)
      localStorage.setItem('cmos_user', JSON.stringify(user))
    } else {
      await refreshUser()
    }
  }, [localToken, refreshUser])

  const logout = useCallback(() => {
    clearSession()
  }, [clearSession])

  return (
    <LocalAuthContext.Provider
      value={{
        isLocalAuth: !!localToken && !!localUser,
        localUser,
        localToken,
        login,
        loginWithPasskey,
        verify2FA,
        finishEnrolment,
        register,
        refreshUser,
        acceptTrialAgreement,
        logout,
      }}
    >
      {children}
    </LocalAuthContext.Provider>
  )
}
